import { authenticatedPost, authenticatedGet, authenticatedFetch } from "../../api/authenticatedFetch";

// askAssistant posts one question and unwraps the "assistant" envelope.
// The backend also returns a conversation_id alongside the assistant
// response; it's attached as _conversationId so SP1 test mocks that resolve
// a bare response object (no conversation_id) keep working unchanged.
export async function askAssistant(question, context = {}, conversationId) {
  const json = await authenticatedPost("/api/v1/assistant/ask", {
    question,
    context,
    conversation_id: conversationId,
  });
  return { ...json.assistant, _conversationId: json.conversation_id };
}

// fetchAssistantCapability reports whether the assistant is usable (LLM configured).
export async function fetchAssistantCapability() {
  const json = await authenticatedGet("/api/v1/assistant/capability");
  return json.capability || { enabled: false };
}

// parseSSEChunk splits an accumulated SSE text buffer into complete frames
// (terminated by a blank line, "\n\n") plus the trailing partial frame that
// hasn't arrived in full yet ("rest", to be prepended to the next chunk).
// Pure and stream-agnostic so it's unit-testable without a real fetch.
//
// The backend (assistant_stream_handlers.go) writes exactly one `event:`
// line and one single-line `data:` line per frame, so this doesn't attempt
// to join multi-line data payloads per the full SSE spec — just what the
// wire format actually sends.
export function parseSSEChunk(buffer) {
  const frames = [];
  let rest = buffer;
  let boundary;
  while ((boundary = rest.indexOf("\n\n")) !== -1) {
    const rawFrame = rest.slice(0, boundary);
    rest = rest.slice(boundary + 2);
    let event = null;
    let data = null;
    for (const line of rawFrame.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data = line.slice(5).trim();
    }
    if (event) frames.push({ event, data });
  }
  return { frames, rest };
}

// askAssistantStream posts one question to the SSE endpoint and dispatches
// callbacks as frames arrive: onStage(stage) per `status` frame,
// onAnswer(askResponse) for the `answer` frame (the bare AskResponse — no
// {assistant, conversation_id} envelope, unlike the sync endpoint), and
// onDone({conversation_id}) for the terminal `done` frame.
//
// Uses `fetch` via authenticatedFetch (not EventSource — EventSource can't
// POST) so the auth header + token-refresh handling is identical to every
// other authenticated call; authenticatedFetch returns the raw Response,
// which is what's needed here to read the body as a stream instead of JSON.
export async function askAssistantStream(question, context = {}, conversationId, callbacks = {}) {
  const { onStage, onAnswer, onDone } = callbacks;
  const response = await authenticatedFetch("/api/v1/assistant/ask/stream", {
    method: "POST",
    body: { question, context, conversation_id: conversationId },
  });
  const reader = response.body?.getReader?.();
  if (!reader) {
    throw new Error("Streaming is not supported in this environment");
  }
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done: streamDone } = await reader.read();
    if (streamDone) break;
    buffer += decoder.decode(value, { stream: true });
    const { frames, rest } = parseSSEChunk(buffer);
    buffer = rest;
    for (const frame of frames) {
      if (!frame.data) continue;
      let payload;
      try {
        payload = JSON.parse(frame.data);
      } catch (_) {
        continue; // skip an unparsable frame rather than aborting the stream
      }
      if (frame.event === "status") onStage?.(payload.stage);
      else if (frame.event === "answer") onAnswer?.(payload);
      else if (frame.event === "done") onDone?.({ conversation_id: payload.conversation_id });
    }
  }
}
